import React from 'react'
import { ContextNotification, Modal } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import { formatDate, formatTime } from '../../utils/dateUtils'
import type { ReservationCalendarForResident } from './types'

export interface ReservationActionSuccessProps {
  calendar: ReservationCalendarForResident
  isRecurring: boolean
  action: 'create' | 'cancel' | 'terminate'
  onClose: () => void
  reservation: {
    startsAt: string
    endsAt: string
  }
}

export const ReservationActionSuccess: React.FC<
  ReservationActionSuccessProps
> = ({ calendar, isRecurring, action, onClose, reservation }) => {
  const { t, i18n } = useTranslation(['common', 'reservations'])

  const slotDateTime = DateTime.fromISO(reservation.startsAt)
  const weekdayName = slotDateTime.toFormat('cccc', { locale: i18n.language })
  const startTime = formatTime(reservation.startsAt)
  const endTime = formatTime(reservation.endsAt)
  const timeRange = `${startTime} - ${endTime}`
  const startDate = formatDate(reservation.startsAt)

  const titleKey =
    action === 'create'
      ? 'reservations:success.title'
      : action === 'terminate'
        ? 'reservations:cancelModal.successTerminated'
        : isRecurring
          ? 'reservations:cancelModal.successRecurring'
          : 'reservations:cancelModal.successSingle'

  const endText =
    action === 'create'
      ? '.'
      : action === 'terminate'
        ? ` ${t('reservations:timeFormatting.hasTerminated')}.`
        : ` ${t('reservations:timeFormatting.hasCancelled')}.`

  return (
    <>
      <Modal.Notifications>
        <ContextNotification textAlign="center" type="success">
          <div className="text-center">
            <h4 className="text-xl font-semibold">{t(titleKey)}</h4>
            <div>
              <p>{calendar.name}</p>
              <p>
                {isRecurring && (
                  <>
                    {t('reservations:timeFormatting.everyWeek')}
                    {'\u00A0'}
                  </>
                )}
                <strong>
                  {t('reservations:timeFormatting.weekdayWithTimeRange', {
                    weekday: weekdayName,
                    timeRange,
                  })}
                  {'\u00A0'}
                  {t('reservations:timeFormatting.startingFrom')} {startDate}
                </strong>
                {endText}
              </p>
            </div>
          </div>
        </ContextNotification>
      </Modal.Notifications>
      <Modal.Buttons>
        <Button variant="cancel" onClick={onClose}>
          {t('common:closeButton')}
        </Button>
      </Modal.Buttons>
    </>
  )
}
